/**
 * Simple cost forecast from historical OPEX/CAPEX.
 * Uses linear trend (last N months) to predict next periods.
 * @param {Array<{ month: string, opex: number, capex: number }>} costHistory
 * @param {number} periodsAhead - number of periods to predict (e.g. 3 for next 3 months)
 * @returns {{ predictedOpex: number[], predictedCapex: number[], recommendation: string }}
 */
export function predictCosts(costHistory, periodsAhead = 3) {
  if (!costHistory || costHistory.length < 2) {
    const last = costHistory?.[0];
    return {
      predictedOpex: Array(periodsAhead).fill(last?.opex ?? 0),
      predictedCapex: Array(periodsAhead).fill(last?.capex ?? 0),
      recommendation: 'Přidejte více historických dat pro predikci.',
    };
  }

  const n = costHistory.length;
  const opexValues = costHistory.map((d) => d.opex);
  const capexValues = costHistory.map((d) => d.capex);

  // Linear trend: y = a + b*x (x = 0..n-1)
  const sumX = (n * (n - 1)) / 2;
  const sumYOpex = opexValues.reduce((s, y) => s + y, 0);
  const sumYCapex = capexValues.reduce((s, y) => s + y, 0);
  let sumXYOpex = 0;
  let sumXYCapex = 0;
  let sumX2 = 0;
  for (let i = 0; i < n; i++) {
    sumXYOpex += i * opexValues[i];
    sumXYCapex += i * capexValues[i];
    sumX2 += i * i;
  }

  const slopeOpex = (n * sumXYOpex - sumX * sumYOpex) / (n * sumX2 - sumX * sumX) || 0;
  const slopeCapex = (n * sumXYCapex - sumX * sumYCapex) / (n * sumX2 - sumX * sumX) || 0;
  const avgOpex = sumYOpex / n;
  const avgCapex = sumYCapex / n;
  const interceptOpex = avgOpex - slopeOpex * ((n - 1) / 2);
  const interceptCapex = avgCapex - slopeCapex * ((n - 1) / 2);

  const predictedOpex = [];
  const predictedCapex = [];
  for (let i = 0; i < periodsAhead; i++) {
    predictedOpex.push(Math.round(interceptOpex + slopeOpex * (n + i)));
    predictedCapex.push(Math.round(Math.max(0, interceptCapex + slopeCapex * (n + i))));
  }

  const nextOpex = predictedOpex[0] ?? avgOpex;
  const lastOpex = opexValues[n - 1];
  const trendUp = nextOpex > lastOpex;
  const recommendation = trendUp
    ? 'OPEX roste – zvažte investice do výměny kritických zařízení a údržby.'
    : 'Náklady stabilní – držet současný režim údržby.';

  return {
    predictedOpex,
    predictedCapex,
    recommendation,
  };
}

/**
 * Format next period label from last month in costHistory (e.g. "2024-02" -> "2024-03").
 */
export function getNextPeriodLabels(costHistory, count) {
  if (!costHistory?.length) return [];
  const last = costHistory[costHistory.length - 1].month; // "YYYY-MM"
  const [y, m] = last.split('-').map(Number);
  const labels = [];
  for (let i = 1; i <= count; i++) {
    let month = m + i;
    let year = y;
    while (month > 12) {
      month -= 12;
      year += 1;
    }
    labels.push(`${year}-${String(month).padStart(2, '0')}`);
  }
  return labels;
}
